import type { InstanceGroupSummary } from "@keydials/api-client";
import { Icon } from "../components/Icon";

export type Band = "Red" | "Amber" | "Green" | null;

/** A RAG band → the app's status colour vars (matching the dashboard's usage). */
export function bandColour(band: Band): string {
  switch (band) {
    case "Green":
      return "var(--st-done)";
    case "Amber":
      return "var(--st-risk)";
    case "Red":
      return "var(--st-overdue)";
    default:
      return "var(--text-faint)";
  }
}

/** Score (0..1) → a RAG-ish colour in thirds, used for the composite when no explicit band is given. */
function scoreColour(score: number | null): string {
  if (score == null) return "var(--text-faint)";
  if (score < 1 / 3) return "var(--st-overdue)";
  if (score < 2 / 3) return "var(--st-risk)";
  return "var(--st-done)";
}

/** A composite health pill: a coloured dot + the named tier + score %. */
export function ScoreBadge({ score, classification, band }: { score: number | null; classification: string | null; band?: Band }) {
  if (score == null && !classification) {
    return <span style={{ fontSize: 12.5, color: "var(--text-faint)", fontWeight: 600 }}>No data yet</span>;
  }
  const colour = band !== undefined ? bandColour(band) : scoreColour(score);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 700 }}>
      <span style={{ width: 9, height: 9, borderRadius: 99, background: colour, flex: "none" }} />
      <span style={{ color: "var(--text)" }}>{classification ?? "—"}</span>
      {score != null && <span style={{ color: "var(--text-muted)" }}>· {Math.round(score * 100)}%</span>}
    </span>
  );
}

/** A dashboard card for a group view (instance-first group). */
export function GroupCard({ summary, onOpen }: { summary: InstanceGroupSummary; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="kd-card kd-card-hover kd-focusable"
      style={{
        padding: "var(--pad-card)",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        font: "inherit",
        color: "inherit",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span
          style={{
            width: 44,
            height: 44,
            flex: "none",
            borderRadius: "var(--radius)",
            background: "var(--surface-2)",
            border: "var(--border-w) solid var(--border)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-muted)",
          }}
        >
          <Icon name="layers" size={22} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {summary.name}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-faint)", marginTop: 3, fontWeight: 600 }}>
            {summary.memberCount} {summary.memberCount === 1 ? "dial" : "dials"} · {summary.rollupStrategy}
          </div>
        </div>
      </div>
      <ScoreBadge score={summary.score} classification={summary.classification} />
    </button>
  );
}
